/**
 * Individual conversation item component with long-press support
 * Separated to allow proper hook usage (hooks can't be called in loops)
 *
 * Extracted verbatim from layout/Sidebar.tsx (behavior-preserving). Re-exported
 * from Sidebar.tsx so the symbol identity is unchanged for any in-module
 * consumer.
 */

import { EllipsisHorizontalIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PinIcon as PinSolidIcon } from '@sidekickicons/react/24/solid';
import { isConversationPinned } from '@/types/conversations';
import { useLongPress } from '@/common/hooks/useLongPress';
import type { ConversationItemWithLongPressProps } from './types';

export function ConversationItemWithLongPress({
  conversation,
  index,
  isActive,
  isDragging,
  isActionsMenuOpen,
  isDragMode,
  isMobile,
  isReadOnly,
  renamingConversationId,
  editingConversationNameRefs,
  onConversationClick,
  onConversationLongPress,
  onConversationContextMenu,
  onConversationActionsClick,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onArchiveConversation,
  onDeleteConversation,
  onSaveConversationName,
  onConversationNameKeyDown,
}: ConversationItemWithLongPressProps) {
  // Create long press handlers for this conversation item
  // ONLY enable long-press on mobile devices, not desktop
  const isPinnedConversation = isConversationPinned(conversation);
  const longPressHandlers = useLongPress({
    onLongPress: () => onConversationLongPress(conversation),
    onClick: () => onConversationClick(conversation),
    enabled: isMobile && !isDragMode && !isReadOnly,
  });
  const isRenamingConversation = conversation.id === renamingConversationId;

  return (
    <div
      className={`conversation-item ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''} ${isActionsMenuOpen ? 'actions-open' : ''} ${isRenamingConversation ? 'renaming' : ''}`}
      draggable={isDragMode && !isMobile}
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onContextMenu={(e) => !isReadOnly && onConversationContextMenu(e, conversation)}
      onClick={!isMobile ? () => onConversationClick(conversation) : undefined}
      {...(isMobile ? longPressHandlers : {})}
    >
      {isDragMode && (
        <div className="drag-handle">
          ⋮⋮
        </div>
      )}
      <div className={`conversation-info ${isRenamingConversation ? 'renaming' : ''}`}>
        <span
          ref={(el) => { editingConversationNameRefs.current[conversation.id] = el; }}
          className={`conversation-name ${isRenamingConversation ? 'renaming' : ''}`}
          contentEditable={isRenamingConversation}
          suppressContentEditableWarning
          spellCheck={false}
          onBlur={(e) => isRenamingConversation && onSaveConversationName(conversation, e.currentTarget)}
          onKeyDown={(e) => isRenamingConversation && onConversationNameKeyDown(e, conversation)}
        >
          {conversation.name}
        </span>
      </div>
      {isPinnedConversation && !isDragMode && (
        <span className="conversation-pinned-indicator" aria-hidden="true">
          <PinSolidIcon className="conversation-pinned-icon" />
        </span>
      )}
      {/* Mobile: an always-visible delete button (the hover-only "…" actions
          button below never appears on touch). Opens a confirmation dialog
          before permanently deleting the chat. */}
      {isMobile && !isDragMode && !isReadOnly && onDeleteConversation && (
        <button
          className="conversation-mobile-delete-btn"
          // The row's long-press gesture listens on touchstart/touchend +
          // mousedown/mouseup (NOT click), so a plain onClick here still let the
          // tap bubble to the row and open the chat. Stop every gesture event the
          // row listens on, and fire the delete directly on touchend (with
          // preventDefault to suppress the ghost click) so a real tap opens the
          // confirm instead of the chat. onClick covers mouse/desktop.
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDeleteConversation(conversation);
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteConversation(conversation);
          }}
          title="Delete conversation"
          aria-label={`Delete conversation ${conversation.name}`}
        >
          <TrashIcon className="conversation-mobile-delete-icon" aria-hidden="true" />
        </button>
      )}
      {!isDragMode && !isReadOnly && (
        <button
          className={`conversation-actions-btn ${isActionsMenuOpen ? 'open' : ''}`}
          onClick={(e) => onConversationActionsClick(e, conversation)}
          title="Conversation actions"
          aria-label={`Conversation actions for ${conversation.name}`}
        >
          <EllipsisHorizontalIcon className="conversation-actions-icon" aria-hidden="true" />
        </button>
      )}
      {isDragMode && (
        <button
          className="delete-conversation-btn"
          onClick={(e) => onArchiveConversation(e, conversation)}
          title="Archive conversation"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 8V21H3V8"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M23 3H1V8H23V3Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 12H14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
